// Pure helpers for the Clinic program and Calls dashboard tabs.

import type {
  DailyWindow,
  DeferredCounts,
  GlobalCaps,
  ProgramConfigPatch,
  ProgramStatus,
  RestartCandidate,
  RestartResult,
  RunSummary,
} from "../types/outreach-program.types"
import { parseApiTimestamp } from "@/lib/datetime"

export const CENTRAL_TZ = "America/Chicago"

/** The outreach policy's hard window: weekdays 08:00-19:00 Central. The UI clips to it too. */
export const WINDOW_EARLIEST = "08:00"
export const WINDOW_LATEST = "19:00"
export const ALLOWED_WEEKDAYS = [0, 1, 2, 3, 4] // 0 = Monday
export const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/

const toMinutes = (t: string) => {
  const m = HHMM.exec(t)
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

const fromMinutes = (n: number) =>
  `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`

/**
 * Clamp a window into 08:00-19:00. Returns null when the input is malformed or
 * nothing of it is left inside the allowed window (start >= end after clipping).
 */
export function clipWindow(w: DailyWindow): DailyWindow | null {
  const s = toMinutes(w.start)
  const e = toMinutes(w.end)
  if (s === null || e === null) return null
  const lo = toMinutes(WINDOW_EARLIEST)!
  const hi = toMinutes(WINDOW_LATEST)!
  const cs = Math.max(s, lo)
  const ce = Math.min(e, hi)
  if (cs >= ce) return null
  return { start: fromMinutes(cs), end: fromMinutes(ce) }
}

/** Keep only Mon-Fri, sorted and de-duplicated. */
export function clipWeekdays(days: number[]): number[] {
  return Array.from(new Set(days.filter((d) => ALLOWED_WEEKDAYS.includes(d)))).sort((a, b) => a - b)
}

/** Weights must be non-negative integers summing to 100, and arm A must exist. */
export function weightsError(weights: Record<string, number>): string | null {
  const values = Object.values(weights)
  if (!("A" in weights)) return "Arm A must have a weight."
  if (values.some((v) => !Number.isInteger(v) || v < 0)) return "Weights must be whole numbers, 0 or more."
  const sum = values.reduce((a, b) => a + b, 0)
  if (sum !== 100) return `Weights must add up to 100 (now ${sum}).`
  return null
}

export const sumValues = (o: Record<string, number | undefined> | null | undefined) =>
  Object.values(o ?? {}).reduce<number>((a, b) => a + (b ?? 0), 0)

export const DEFERRED_LABELS: Record<string, string> = {
  day_cap: "day cap",
  bucket_cap: "15-min cap",
  text_cap: "text cap",
  concurrency: "concurrency",
}

export function describeCounts(counts: Record<string, number | undefined> | null | undefined, labels: Record<string, string> = {}) {
  return Object.entries(counts ?? {})
    .filter(([, n]) => (n ?? 0) > 0)
    .map(([k, n]) => `${n} ${labels[k] ?? k.replace(/_/g, " ")}`)
    .join(", ")
}

export interface RunSummaryDigest {
  booked: number
  planned: number
  deferred: number
  skipped: number
  errors: number
  manual: boolean
  /** Something was deferred, skipped or failed: the toast must not read as a plain success. */
  partial: boolean
  title: string
  description: string
}

/** One line per run for toasts: booked / deferred (by cap) / skipped (by reason) / errors. */
export function digestRunSummary(summary: RunSummary | null | undefined, what: string): RunSummaryDigest {
  const booked = summary?.booked?.length ?? 0
  const planned = summary?.planned ?? 0
  const deferred = sumValues(summary?.deferred as DeferredCounts)
  const skipped = sumValues(summary?.skipped)
  const errors = summary?.errors?.length ?? 0
  const manual = summary?.mode === "manual"
  const shadow = summary?.mode === "shadow" || summary?.mode === "paused"
  const parts = [shadow ? `${planned} planned (nothing booked: ${summary?.mode})` : `${booked} booked`]
  if (deferred) parts.push(`${deferred} deferred (${describeCounts(summary?.deferred, DEFERRED_LABELS)})`)
  if (skipped) parts.push(`${skipped} skipped (${describeCounts(summary?.skipped)})`)
  if (errors) parts.push(`${errors} failed`)
  return {
    booked,
    planned,
    deferred,
    skipped,
    errors,
    manual,
    partial: deferred + skipped + errors > 0,
    title: `${what}${manual ? " (manual run)" : ""}: ${errors ? "finished with failures" : deferred + skipped ? "partly done" : "done"}`,
    description: parts.join(" · "),
  }
}

/**
 * The generic action toast (generate care plans, upload, retry...): report
 * failed/skipped counts the server sends instead of a plain "success".
 */
export function describeActionResult(name: string, result: unknown): { success: boolean; message: string } {
  if (!result || typeof result !== "object") return { success: true, message: `${name} completed` }
  const r = result as Record<string, unknown>
  const count = (v: unknown): number =>
    typeof v === "number" ? v : Array.isArray(v) ? v.length : v && typeof v === "object" ? sumValues(v as Record<string, number>) : 0
  const failed = count(r.failed) + count(r.failed_patients) + count(r.errors) + count(r.enrollment_errors)
  const skipped = count(r.skipped) + count(r.patients_skipped)
  const parts: string[] = []
  if (failed) parts.push(`${failed} failed`)
  if (skipped) parts.push(`${skipped} skipped`)
  if (!parts.length) return { success: true, message: `${name} completed` }
  return { success: failed === 0, message: `${name} finished with problems: ${parts.join(", ")}` }
}

export function formatCentral(iso: string | null | undefined, withDate = true): string {
  if (!iso) return "-"
  const d = parseApiTimestamp(iso)
  if (!d) return iso
  return new Intl.DateTimeFormat("en-US", {
    timeZone: CENTRAL_TZ,
    ...(withDate ? { month: "short", day: "numeric" } : {}),
    hour: "numeric",
    minute: "2-digit",
  }).format(d)
}

export function todayCentral(now: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone: CENTRAL_TZ }).format(now)
}

const asCandidate = (c: RestartCandidate | string): RestartCandidate =>
  typeof c === "string" ? { patient_id: c } : c

export function restartCandidates(r: RestartResult | null | undefined): { count: number; list: RestartCandidate[] } {
  if (!r) return { count: 0, list: [] }
  if (typeof r.candidates === "number") return { count: r.candidates, list: [] }
  const list = (r.candidates ?? []).map(asCandidate)
  return { count: list.length, list }
}

export const restartedList = (r: RestartResult | null | undefined): RestartCandidate[] =>
  (r?.restarted ?? []).map(asCandidate)

/**
 * A readable message from a failed request. FastAPI sends `detail` as a string
 * (graham's 400/404), a list of {loc, msg} (422 body validation) or an object;
 * rendering the raw list would crash React, so it is flattened here.
 */
export function apiErrorMessage(err: unknown, fallback = "Request failed"): string {
  const e = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string } | null
  const detail = e?.response?.data?.detail
  const status = e?.response?.status
  let text: string | null = null
  if (typeof detail === "string") text = detail
  else if (Array.isArray(detail)) {
    text = detail
      .map((d: any) => {
        if (typeof d === "string") return d
        const loc = Array.isArray(d?.loc) ? d.loc.filter((x: unknown) => x !== "body").join(".") : ""
        return loc ? `${loc}: ${d?.msg ?? JSON.stringify(d)}` : String(d?.msg ?? JSON.stringify(d))
      })
      .join("; ")
  } else if (detail && typeof detail === "object") {
    // graham's program PATCH: 422 {message, problems: [...]}.
    const d = detail as { message?: unknown; problems?: unknown }
    const problems = Array.isArray(d.problems) ? d.problems.map(String) : []
    const message = typeof d.message === "string" ? d.message : null
    text = problems.length ? `${message ? `${message}: ` : ""}${problems.join("; ")}` : message ?? JSON.stringify(detail)
  }
  if (!text) text = e?.message || fallback
  return status && status >= 400 && status < 500 ? `${text} (HTTP ${status})` : text
}

// ─── Program settings form ───────────────────────────────────────────

export interface ProgramForm {
  maxPerDay: string
  per15: string
  windows: DailyWindow[]
  days: number[]
  weights: Record<string, string>
  spanishEnabled: boolean
}

/** The form's starting values: the EFFECTIVE config the server returned (defaults applied, windows clipped). */
export function formFromProgram(program: ProgramStatus): ProgramForm {
  const ab = program.reminder_ab
  const armNames = Object.keys(ab?.arms ?? {})
  const w = ab?.weights ?? {}
  return {
    maxPerDay: String(program.caps?.max_calls_per_day ?? ""),
    per15: String(program.caps?.calls_per_15min ?? ""),
    windows: (program.call_windows?.daily_windows ?? []).map((x) => ({ start: x.start, end: x.end })),
    days: [...(program.call_windows?.allowed_days_of_week ?? [])].sort((a, b) => a - b),
    weights: Object.fromEntries(armNames.map((a) => [a, String(w[a] ?? 0)])),
    spanishEnabled: !!ab?.spanish_enabled,
  }
}

const isWholeNumber = (v: string) => /^\d+$/.test(v.trim())

/**
 * Client-side checks that mirror graham's
 * validate_patch (services/outreach/program.py): windows HH:MM inside
 * 08:00-19:00 with start before end, Mon-Fri only, caps whole numbers above 0
 * and within the API's global limits (when caps.global sends them), weights
 * whole numbers >= 0 adding up to 100, no weight on an arm without text.
 * Returns field -> message.
 */
export function validateProgramForm(
  form: ProgramForm,
  global?: Partial<GlobalCaps> | null,
  armTexts: Record<string, string> = {},
): Record<string, string> {
  const errors: Record<string, string> = {}
  const mpd = Number(form.maxPerDay)
  const p15 = Number(form.per15)
  const dayLimit = global?.max_calls_per_day
  const bucketLimit = global?.calls_per_15min

  if (!isWholeNumber(form.maxPerDay) || mpd <= 0) errors.maxPerDay = "Daily call cap must be a whole number above 0."
  else if (dayLimit != null && mpd > dayLimit) errors.maxPerDay = `Daily call cap can be at most ${dayLimit} (global limit).`

  if (!isWholeNumber(form.per15) || p15 <= 0) errors.per15 = "Calls per 15 minutes must be a whole number above 0."
  else if (bucketLimit != null && p15 > bucketLimit) errors.per15 = `Calls per 15 minutes can be at most ${bucketLimit} (global limit).`

  if (form.windows.length === 0) errors.windows = "Add at least one call window."
  form.windows.forEach((w, i) => {
    const key = `window${i}`
    if (!HHMM.test(w.start) || !HHMM.test(w.end)) errors[key] = `Window ${i + 1}: use HH:MM times.`
    else if (w.start < WINDOW_EARLIEST || w.end > WINDOW_LATEST)
      errors[key] = `Window ${i + 1} must be inside ${WINDOW_EARLIEST}-${WINDOW_LATEST} Central.`
    else if (w.start >= w.end) errors[key] = `Window ${i + 1}: start must be before end.`
  })

  if (form.days.some((d) => !ALLOWED_WEEKDAYS.includes(d))) errors.days = "Weekdays must be Monday to Friday only."
  else if (form.days.length === 0) errors.days = "Pick at least one weekday."

  const arms = Object.keys(form.weights)
  if (arms.length) {
    if (!("A" in form.weights)) errors.weights = "Arm A must have a weight."
    else if (arms.some((a) => !isWholeNumber(form.weights[a]))) errors.weights = "Weights must be whole numbers, 0 or more."
    else {
      const sum = arms.reduce((n, a) => n + Number(form.weights[a]), 0)
      const noText = arms.filter((a) => Number(form.weights[a]) > 0 && !(armTexts[a] ?? "").trim())
      if (sum !== 100) errors.weights = `Weights must add up to 100 (now ${sum}).`
      else if (noText.length) errors.weights = `Arm ${noText.join(", ")} has no text; give it weight 0.`
    }
  }
  return errors
}

const sameWindows = (a: DailyWindow[], b: DailyWindow[]) =>
  a.length === b.length && a.every((w, i) => w.start === b[i].start && w.end === b[i].end)
const sameList = (a: number[], b: number[]) => a.length === b.length && a.every((x, i) => x === b[i])

/**
 * PATCH /program `config` with only the sections that changed, in graham's
 * CampaignConfig shape (program.max_calls_per_day, pacing.calls_per_15min,
 * call_windows, reminder_ab.weights / spanish_enabled). Null when nothing changed.
 */
export function buildConfigPatch(initial: ProgramForm, form: ProgramForm): ProgramConfigPatch | null {
  const patch: ProgramConfigPatch = {}
  if (form.maxPerDay.trim() !== initial.maxPerDay) patch.program = { max_calls_per_day: Number(form.maxPerDay) }
  if (form.per15.trim() !== initial.per15) patch.pacing = { calls_per_15min: Number(form.per15) }
  const days = [...form.days].sort((a, b) => a - b)
  if (!sameWindows(form.windows, initial.windows) || !sameList(days, initial.days))
    patch.call_windows = { daily_windows: form.windows.map((w) => ({ start: w.start, end: w.end })), allowed_days_of_week: days }
  const weightsChanged = Object.keys(form.weights).some((a) => form.weights[a].trim() !== initial.weights[a])
  if (weightsChanged || form.spanishEnabled !== initial.spanishEnabled) {
    patch.reminder_ab = {}
    if (weightsChanged)
      patch.reminder_ab.weights = Object.fromEntries(Object.entries(form.weights).map(([a, v]) => [a, Number(v)]))
    if (form.spanishEnabled !== initial.spanishEnabled) patch.reminder_ab.spanish_enabled = form.spanishEnabled
  }
  return Object.keys(patch).length ? patch : null
}
